import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Filter,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { getAllProperties, deleteProperty } from '@/services/propertyService';
import { cn } from '@/lib/utils';

/**
 * İlan durum badge bileşeni
 */
function PropertyStatusBadge({ isActive, status }) {
  if (!isActive) {
    return (
      <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-200">
        Pasif
      </Badge>
    );
  }
  
  if (status === 'satilik') {
    return (
      <Badge variant="default" className="bg-green-100 text-green-700 border-green-200">
        Aktif
      </Badge>
    );
  }
  
  return (
    <Badge variant="default" className="bg-blue-100 text-blue-700 border-blue-200">
      Aktif
    </Badge>
  );
}

/**
 * İlan tipi badge bileşeni
 */
function PropertyTypeBadge({ type }) {
  const variants = {
    villa: { label: 'Villa', className: 'bg-purple-100 text-purple-700 border-purple-200' },
    apartman: { label: 'Apartman', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    arsa: { label: 'Arsa', className: 'bg-green-100 text-green-700 border-green-200' },
    ticari: { label: 'Ticari', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  };
  const config = variants[type] || variants.apartman;
  return <Badge className={cn('border', config.className)}>{config.label}</Badge>;
}

/**
 * İlan satış/kiralık badge bileşeni
 */
function PropertyListingTypeBadge({ status }) {
  return status === 'satilik' ? (
    <Badge variant="default" className="bg-emerald-100 text-emerald-700 border-emerald-200">
      Satılık
    </Badge>
  ) : (
    <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-amber-200">
      Kiralık
    </Badge>
  );
}

/**
 * Tarih formatlama
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Fiyat formatlama
 */
function formatPrice(price) {
  if (!price) return '-';
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Tablo skeleton loader
 */
function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-16 w-24 rounded" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-48" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-12" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <div className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * StaffPropertiesPage - İlan yönetimi listesi sayfası
 */
const StaffPropertiesPage = () => {
  const { toast } = useToast();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  
  // Filtreler
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Sayfalama
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllProperties();
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'İlanlar yüklenirken bir hata oluştu.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Filtrelenmiş ve sıralanmış ilanlar
  const filteredProperties = useMemo(() => {
    let filtered = [...properties];

    // Arama filtresi
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title_tr?.toLowerCase().includes(query) ||
          p.title_en?.toLowerCase().includes(query) ||
          p.location_tr?.toLowerCase().includes(query) ||
          p.location_en?.toLowerCase().includes(query)
      );
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter((p) => p.is_active);
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter((p) => !p.is_active);
      }
    }

    // Tip filtresi
    if (typeFilter !== 'all') {
      if (typeFilter === 'satilik' || typeFilter === 'kiralik') {
        filtered = filtered.filter((p) => p.status === typeFilter);
      } else {
        filtered = filtered.filter((p) => p.type === typeFilter);
      }
    }

    // Sıralama
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'price_asc':
          return (a.price_gbp || 0) - (b.price_gbp || 0);
        case 'price_desc':
          return (b.price_gbp || 0) - (a.price_gbp || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [properties, searchQuery, statusFilter, typeFilter, sortBy]);

  // Sayfalama
  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
  const paginatedProperties = filteredProperties.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDeleteClick = (property) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propertyToDelete) return;

    try {
      await deleteProperty(propertyToDelete.id);
      toast({
        title: 'Başarılı',
        description: 'İlan başarıyla silindi.',
      });
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'İlan silinirken bir hata oluştu.',
      });
    } finally {
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">İlan Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Toplam {filteredProperties.length} ilan
          </p>
        </div>
        <Link to="/staff/properties/new">
          <Button className="w-full sm:w-auto">
            <Plus size={18} className="mr-2" />
            Yeni İlan Ekle
          </Button>
        </Link>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Başlık veya adres ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Durum Filtresi */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="inactive">Pasif</SelectItem>
              </SelectContent>
            </Select>

            {/* Tip Filtresi */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Tip" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Tipler</SelectItem>
                <SelectItem value="satilik">Satılık</SelectItem>
                <SelectItem value="kiralik">Kiralık</SelectItem>
                <SelectItem value="villa">Villa</SelectItem>
                <SelectItem value="apartman">Apartman</SelectItem>
                <SelectItem value="arsa">Arsa</SelectItem>
                <SelectItem value="ticari">Ticari</SelectItem>
              </SelectContent>
            </Select>

            {/* Sıralama */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sırala" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Yeni</SelectItem>
                <SelectItem value="oldest">En Eski</SelectItem>
                <SelectItem value="price_asc">Fiyat (Artan)</SelectItem>
                <SelectItem value="price_desc">Fiyat (Azalan)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* İlan Tablosu */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Görsel</TableHead>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead>Fiyat (GBP)</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Oda</TableHead>
                  <TableHead>Alan (m²)</TableHead>
                  <TableHead>Oluşturulma</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableSkeleton />
                ) : paginatedProperties.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <Eye size={48} className="mb-3 opacity-20" />
                        <p className="text-lg font-medium mb-2">
                          {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                            ? 'Sonuç bulunamadı'
                            : 'Henüz ilan eklenmemiş'}
                        </p>
                        {!searchQuery && statusFilter === 'all' && typeFilter === 'all' && (
                          <Link to="/staff/properties/new">
                            <Button variant="outline" className="mt-4">
                              <Plus size={18} className="mr-2" />
                              İlk İlanı Ekle
                            </Button>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProperties.map((property) => (
                    <TableRow key={property.id}>
                      <TableCell>
                        <div className="w-24 h-16 rounded overflow-hidden bg-gray-100">
                          {property.property_images?.[0]?.image_url ? (
                            <img
                              src={property.property_images[0].image_url}
                              alt={property.title_tr || ''}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                              <Eye size={20} />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium text-foreground truncate">
                            {property.title_tr || property.title_en || '-'}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {property.location_tr || property.location_en || '-'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <PropertyTypeBadge type={property.type} />
                          <PropertyListingTypeBadge status={property.status} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          £{formatPrice(property.price_gbp)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <PropertyStatusBadge 
                          isActive={property.is_active} 
                          status={property.status} 
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{property.bedrooms || 0}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{property.sqm || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(property.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/staff/properties/${property.id}`}>
                            <Button variant="outline" size="icon" title="Düzenle">
                              <Edit size={16} />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDeleteClick(property)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Sil"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Önceki
          </Button>
          <div className="flex items-center gap-1">
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page}>...</span>;
              }
              return null;
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Sonraki
          </Button>
        </div>
      )}

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İlanı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. İlan kalıcı olarak silinecektir.
              <br />
              <br />
              <strong>{propertyToDelete?.title_tr || propertyToDelete?.title_en}</strong> başlıklı
              ilanı silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffPropertiesPage;
